import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';
import { SelectAll } from '../SelectAll';
import { Pagination } from '../../Pagination';
import { Refetch } from '@/components/ui/Refetch';
import { Counter } from '../TableMultiSelection/Counter';
import { TableMultiSelection } from '../TableMultiSelection';
import { useResolution886ReportFile } from '@/context/Resolution886ReportFiles';
import { useGetListResolution886Files } from '@/hooks/v2/resolution-886/useGetListResolution886Files';
import type { FormData, Resolution886FileDetails } from '@/context/Resolution886ReportFiles/types';
import type { ColumnsType } from 'antd/lib/table';
import * as S from '../styles';

const { useFormInstance } = Form;

const DEFAULT_LIMIT = 8;

export function FileTable(): JSX.Element {
	const form = useFormInstance();
	const fieldName: keyof FormData = 'file_list_id';

	const { filters, deselectedFiles, isSelectAllChecked, onSetFilter, onSetDeselectedFiles, onSetSelectAll } =
		useResolution886ReportFile();

	const {
		isLoading,
		data: dataFiles,
		isError,
		refetch
	} = useGetListResolution886Files({
		organization_id: filters?.organization_id,
		company_id: filters?.company_id,
		offset: filters?.offset || 1,
		limit: filters?.limit,
		file_name: filters?.name,
		start_date: filters?.start_date,
		end_date: filters?.end_date,
		line_id: filters?.line_id,
		sector_id: filters?.sector_id,
		workstation_id: filters?.workstation_id
	});

	const limit = filters?.limit || DEFAULT_LIMIT;
	const page = filters?.offset ?? 1;
	const rows = dataFiles?.rows ?? [];

	const effectiveTotal = rows.length < limit ? (page - 1) * limit + rows.length : dataFiles?.total;

	function setEvaluationStatus(evaluation: string): string {
		return evaluation === 'NOT EVALUATED' ? I18n.get('Not evaluated') : I18n.get('Evaluated');
	}

	const columns: ColumnsType<Resolution886FileDetails> = [
		{
			width: '50%',
			align: 'center',
			key: 'original_name',
			title: <S.CustomTableTitle>{I18n.get('File')}</S.CustomTableTitle>,
			className: 'file-columns',
			dataIndex: 'original_name',
			render: (text: string) => (
				<S.CustomTooltip title={text}>
					<S.EllipsisText>{text}</S.EllipsisText>
				</S.CustomTooltip>
			)
		},
		{
			width: '25%',
			align: 'center',
			key: 'created_at',
			responsive: ['lg'],
			dataIndex: 'created_at',
			title: <S.CustomTableTitle>{I18n.get('Upload date')}</S.CustomTableTitle>,
			render: (date: string) => (date ? moment(date).format('L') : '-')
		},
		{
			width: '25%',
			align: 'center',
			key: 'evaluationResolution886',
			responsive: ['lg'],
			dataIndex: 'evaluation_status',
			title: <S.CustomTableTitle>{I18n.get('Evaluation Resolution 886')}</S.CustomTableTitle>,
			render: (evaluation: string) => (
				<S.CustomTooltip title={evaluation}>
					<S.EllipsisText>{setEvaluationStatus(evaluation)}</S.EllipsisText>
				</S.CustomTooltip>
			)
		}
	];

	function handleDisableSelectAll(totalDeselected: number): void {
		if (totalDeselected !== dataFiles?.total) return;

		onSetSelectAll(false);
	}
	function handleSetSelectAllForm(totalDeselected: number): void {
		const isChecked = totalDeselected === 0 && isSelectAllChecked;
		form.setFieldValue('select_all_files', isChecked);
	}

	function handleUpdateDeselected(deselected: Set<string>): void {
		handleSetSelectAllForm(deselected.size);
		handleDisableSelectAll(deselected.size);
		onSetDeselectedFiles(deselected);
	}

	function handleOnSelectPage(selectedIds: string[]): void {
		const pageIds = dataFiles?.rows.map((file: any) => file.id);
		const newDeselectedFiles = new Set(deselectedFiles);

		pageIds?.forEach((id: string) => {
			selectedIds.length === 0 && isSelectAllChecked ? newDeselectedFiles.add(id) : newDeselectedFiles.delete(id);
		});

		handleUpdateDeselected(newDeselectedFiles);
	}

	function handleOnChangePage(page: number): void {
		const limit = filters?.limit || DEFAULT_LIMIT;
		onSetFilter({ ...filters, offset: page, limit });
	}

	function handleCurrentPage(offset: number): number {
		return offset ?? 1;
	}

	function handleSetDeselectedFiles(value: any, selected: boolean): void {
		if (!isSelectAllChecked) return;

		const newDeselectedFiles = new Set(deselectedFiles);

		if (!selected) {
			newDeselectedFiles.add(value.id);
		} else {
			newDeselectedFiles.delete(value.id);
		}

		handleUpdateDeselected(newDeselectedFiles);
	}

	const selectedIds = useMemo(() => {
		if (!isSelectAllChecked || !dataFiles?.rows) return [];

		const pageIds = dataFiles.rows.map((file) => file.id);
		const availableIds = pageIds.filter((fileId) => !deselectedFiles.has(fileId));

		return availableIds;
	}, [isSelectAllChecked, dataFiles?.rows, deselectedFiles]);

	useEffect(() => {
		if (!isSelectAllChecked) return;

		form.setFieldValue(fieldName, selectedIds);
	}, [isSelectAllChecked, selectedIds, form, fieldName]);

	if (isError) {
		return <Refetch onClick={refetch} />;
	}

	return (
		<Row gutter={[0, 16]}>
			<Col span={24}>
				<Row justify="space-between" align="middle">
					<Col>
						<Counter total={dataFiles?.total} />
					</Col>
					<Col>
						<SelectAll />
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Form.Item
					noStyle
					name={fieldName}
					rules={[{ required: !isSelectAllChecked, message: I18n.get('Please select at least one file') }]}
				>
					<TableMultiSelection
						columns={columns}
						loading={isLoading}
						data={dataFiles?.rows}
						onSelectPage={handleOnSelectPage}
						onSelect={handleSetDeselectedFiles}
					/>
				</Form.Item>
			</Col>
			<Col span={24}>
				<Pagination
					limit={limit}
					total={effectiveTotal}
					onChangePage={handleOnChangePage}
					currentPage={handleCurrentPage(filters?.offset)}
				/>
			</Col>
		</Row>
	);
}
